import { createFeatureSelector, createSelector } from '@ngrx/store';

import type { IFamily, IFamilyGroupWithIcons } from '../../library/becks-family';
import { FamilyGroupCode } from '../../library/becks-family';
import { getFamilyGroupIconPath, mapFamilyGroupFamilies } from '../../shared/helpers/family.helper';

import type { ProductsState } from '../reducers/products.reducer';
import { familiesAdapter, productsStateKey } from '../reducers/products.reducer';

export const getProductsState = createFeatureSelector<ProductsState>(productsStateKey);

const getProductFamiliesEntity = createSelector(getProductsState, (state) => state.model.families);
export const getProductFamiliesLoaded = createSelector(getProductsState, (state) => state.viewModel.familiesLoaded);
export const getProductFamiliesLoading = createSelector(getProductsState, (state) => state.viewModel.familiesLoading);
export const getProductFamiliesLoadErred = createSelector(
  getProductsState,
  (state) => state.viewModel.familiesLoadErred
);

/* Entity selectors */
const familyEntitySelectors = familiesAdapter.getSelectors();
export const getProductFamilies = createSelector(getProductFamiliesEntity, familyEntitySelectors.selectAll);
export const getProductFamiliesEntities = createSelector(
  getProductFamiliesEntity,
  familyEntitySelectors.selectEntities
);
export const getProductFamiliesExceptFamily75 = createSelector(getProductFamilies, (families) =>
  families.filter((family) => family.ID !== 75)
);

export const getProductTraits = createSelector(getProductsState, (state) => state.model.traits);
export const getProductTraitsLoaded = createSelector(getProductsState, (state) => state.viewModel.traitsLoaded);
export const getProductTraitsLoading = createSelector(getProductsState, (state) => state.viewModel.traitsLoading);
export const getProductTraitsLoadErred = createSelector(getProductsState, (state) => state.viewModel.traitsLoadErred);

export const getProductTreatments = createSelector(getProductsState, (state) => state.model.treatments);
export const getProductTreatmentsLoaded = createSelector(getProductsState, (state) => state.viewModel.treatmentsLoaded);
export const getProductTreatmentsLoading = createSelector(
  getProductsState,
  (state) => state.viewModel.treatmentsLoading
);
export const getProductTreatmentsLoadErred = createSelector(
  getProductsState,
  (state) => state.viewModel.treatmentsLoadErred
);

export const getProductPackageTypes = createSelector(getProductsState, (state) => state.model.packageTypes);
export const getProductPackageTypesLoaded = createSelector(
  getProductsState,
  (state) => state.viewModel.packageTypesLoaded
);
export const getProductPackageTypesLoading = createSelector(
  getProductsState,
  (state) => state.viewModel.packageTypesLoading
);
export const getProductPackageTypesLoadErred = createSelector(
  getProductsState,
  (state) => state.viewModel.packageTypesLoadErred
);

export const getProductGradeSizes = createSelector(getProductsState, (state) => state.model.gradeSizes);
export const getProductGradeSizesLoaded = createSelector(getProductsState, (state) => state.viewModel.gradeSizesLoaded);
export const getProductGradeSizesLoading = createSelector(
  getProductsState,
  (state) => state.viewModel.gradeSizesLoading
);
export const getProductGradeSizesLoadErred = createSelector(
  getProductsState,
  (state) => state.viewModel.gradeSizesLoadErred
);

/** Derived Selectors */
export const getProductFamilyGroups = createSelector(
  getProductFamiliesEntities,
  (entities): IFamilyGroupWithIcons[] => {
    const groups: IFamilyGroupWithIcons[] = [
      {
        code: FamilyGroupCode.corn,
        desc: 'Corn',
        families: mapFamilyGroupFamilies<IFamily>(['CORN'], entities),
        iconPath: getFamilyGroupIconPath(FamilyGroupCode.corn),
      },
      {
        code: FamilyGroupCode.soybeans,
        desc: 'Soybeans',
        families: mapFamilyGroupFamilies<IFamily>(['SOYBEANS'], entities),
        iconPath: getFamilyGroupIconPath(FamilyGroupCode.soybeans),
      },
      {
        code: FamilyGroupCode.wheat,
        desc: 'Wheat',
        families: mapFamilyGroupFamilies<IFamily>(['WHEAT'], entities),
        iconPath: getFamilyGroupIconPath(FamilyGroupCode.wheat),
      },
      {
        code: FamilyGroupCode.otherCrop,
        desc: 'Alfalfa & Other Seed',
        families: mapFamilyGroupFamilies<IFamily>(
          ['OATS SG', 'MILO', 'EL ALF', 'COV CROP', 'FORAGES', 'SWTCORN'],
          entities
        ),
        iconPath: getFamilyGroupIconPath(FamilyGroupCode.otherCrop),
      },
      {
        code: FamilyGroupCode.promo,
        desc: 'Commitment Rewards',
        families: mapFamilyGroupFamilies<IFamily>(['PROMO'], entities),
        iconPath: getFamilyGroupIconPath(FamilyGroupCode.promo),
      },
      {
        code: FamilyGroupCode.nonCrop,
        desc: 'Other Items',
        families: mapFamilyGroupFamilies<IFamily>(
          [
            'BALFWD',
            'GVNC OAT',
            'NRETSEED',
            'ToTAcres',
            'INBRED',
            'BBEDDING',
            'INOCS',
            'PLR BOX',
            'PNR PLTS',
            'SLS AIDS',
            'BLK SOY',
            'WH CORN',
            'WH SOY',
            'WH WHT',
            'SILINOCS',
            'BLK WHT',
            'TP Corn',
            'SHCKBALE',
            'SALESTAX',
            'FRM PRDS',
            'RENTINC',
            'INTINC',
            'NTINTINC',
            'MERCHINC',
            'OTHINC',
            'CCORN',
            'CBEAN',
            'CWHEAT',
            'CATTLE',
            'OTHINCXT',
            'CONTINC',
            'Research',
            'DCDCORN',
            'DCDBEANS',
            'DCDWHEAT',
            'CROP IMG',
            'TECH',
            'EXPENSE',
            'PACKAGE',
            'CR MEMO',
            'SHARED',
            'REFUNDS',
            'MISC',
            'CD CARRY',
            'BONXFER',
            'NOCDADJ',
            'TP SOY',
          ],
          entities
        ),
        iconPath: getFamilyGroupIconPath(FamilyGroupCode.nonCrop),
      },
    ];

    return groups;
  }
);
